<script lang="ts">
	import {
		DataHandlerDevice,
		DeviceGroup,
		DeviceRPi,
		DeviceSupports,
		type Device,
	} from "luxedo-data"
	import { MoonIcon, SettingsIcon } from "luxedo-icons"
	import { Reactivity } from "luxedo-reactivity"
	import DeviceIcon from "../icons/DeviceIcon.svelte"
	import { triggerContextMenuWithGenerator } from "context-menu"
	import { CTXOptions } from "../../../modules/ctx-menu/ContextMenuOptions"
	import { openProjectorMenu } from "../../routes/projectors"
	import { DeviceStatusPreview } from "luxedo-comps"
	import ToggleProjectorPowerButton from "./ToggleProjectorPowerButton.svelte"

	interface Props {
		device: Device
		filterDevice: Device
		hideSettings?: boolean
		onSetDeviceFilter: (device: Device) => void
	}

	let { device, filterDevice, hideSettings = false, onSetDeviceFilter }: Props = $props()

	let deactivated = $state(false)

	$effect.pre(() => {
		Reactivity.useProps(device, ["color", "isOnline"])
		if (device instanceof DeviceGroup) {
			Reactivity.useProps(device, ["childDevices"])
		}
	})

	let color = $derived(Reactivity.propOf(device, "color"))
	let isOnline = $derived(Reactivity.propOf(device, "isOnline"))
	let childDevices = $derived(
		device instanceof DeviceGroup ? Reactivity.propOf(device, "childDevices") : [],
	)
	let isGroupOnline = $state(device.isOnline)

	let showPowerControl = $derived(
		(isOnline && device instanceof DeviceRPi && DeviceSupports.projectorControl(device)) ||
			(isGroupOnline && device instanceof DeviceGroup),
	)

	$effect(() => {
		const listenerIds = childDevices.map((dev) => {
			const listenerId = dev.addUpdateListener((d) => {
				updateDeviceGroupState()
			})
			return [dev.id, listenerId]
		})

		return () => {
			listenerIds.forEach(([devId, listenerId]) => {
				DataHandlerDevice.get(Number(devId)).removeUpdateListener(String(listenerId))
			})
		}
	})

	function updateDeviceGroupState() {
		isGroupOnline = device.isOnline
		deactivated = device.isDeactivated
	}

	function setFilterDevice() {
		if (filterDevice === device) return onSetDeviceFilter(undefined)

		onSetDeviceFilter(device)
	}

	function openProjectorSettings(e: MouseEvent) {
		e.stopPropagation()
		openProjectorMenu(device, "About")
	}
</script>

<div
	class="projector-filter-instance {filterDevice === device ? 'isSelected' : ''} {deactivated
		? 'deactivated'
		: ''}"
	style="--color-device: {color};"
	role="button"
	tabindex="0"
	onkeydown={(e) => {
		if (e.key === "Enter") setFilterDevice()
	}}
	oncontextmenu={triggerContextMenuWithGenerator(CTXOptions.Device.createOptions, [device])}
	onclick={setFilterDevice}
>
	<div class="projector-icon">
		<DeviceIcon {device} />
	</div>
	<div class="projector-info">
		<DeviceStatusPreview {device} showControls={false} />
	</div>
	<div class="setting-buttons">
		{#if showPowerControl}
			<ToggleProjectorPowerButton device={device as DeviceRPi} />
		{/if}
		{#if !hideSettings}
			<button onclick={openProjectorSettings} class="icon settings-icon">
				<SettingsIcon />
			</button>
		{/if}
	</div>
</div>

<style>
	.projector-filter-instance {
		position: relative;
		box-sizing: border-box;
		cursor: pointer;
		display: flex;
		background-color: var(--color-bg);
		margin: 2px;
		margin-bottom: 0.5rem;
		border-radius: var(--br);
		align-items: center;
		height: 4rem;
		border: 0;
		width: 100%;
		transition:
			filter 250ms,
			color 250ms,
			box-shadow 250ms;
		box-shadow: var(--shadow-small);
	}

	.projector-filter-instance:hover,
	.projector-filter-instance:focus-visible {
		color: var(--color-device);
	}

	.projector-filter-instance.deactivated {
		opacity: 0.6;
		transition:
			opacity 500ms,
			border-color 500ms,
			color 500ms;
		color: var(--color-text);
	}

	.projector-filter-instance.deactivated:hover,
	.projector-filter-instance.deactivated:focus-visible,
	.projector-filter-instance.deactivated.isSelected {
		opacity: 1;
		color: var(--color-text-light);
	}

	.projector-filter-instance.deactivated :global(.wake-button) {
		display: none;
	}

	.projector-filter-instance.isSelected {
		box-shadow: var(--shadow-large);
		outline: 3px solid var(--color-device);
		outline-offset: -3px;
	}

	.projector-info {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		overflow: hidden;
	}

	.projector-icon {
		padding: 0 0.5rem 0 0.75rem;
		height: fit-content;
	}

	.projector-icon :global(.projector-icon) {
		width: 2rem;
		margin-top: 0.3rem;
	}

	.setting-buttons {
		margin-right: 0.5rem;
		display: flex;
		flex-direction: row;
		gap: 0;
		align-items: center;
	}

	.setting-buttons :global(svg) {
		height: 1.5rem;
		width: auto;
	}

	.settings-icon :global(.svg-fill) {
		fill: var(--color-text);
	}

	.settings-icon :global(.svg-stroke) {
		stroke: var(--color-text);
	}

	.settings-icon:hover :global(.svg-stroke),
	.settings-icon:focus-visible :global(.svg-stroke) {
		stroke: var(--color-text-light);
	}

	.projector-filter-instance .projector-info :global(.device-name) {
		font-size: 1rem;
		font-family: var(--font-body);
	}

	.projector-info :global(.projector-status) {
		font-size: var(--text-small);
	}

	.projector-info :global(.projector-status-indicator) {
		margin-right: 0.25rem;
	}
</style>
